import { gql, useQuery } from "@apollo/client";
import { LoadingIndicator } from "@resource/atlas";
import GuideTemplateMapper, {
  Stage,
} from "components/GuideTemplateMapper/GuideTemplateMapper";
import {
  GetAtsStagesForMapping,
  GetAtsStagesForMappingVariables,
  GetPlanForMapping,
  GetPlanForMappingVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";

const GET_ATS_STAGES_FOR_MAPPING = gql`
  query GetAtsStagesForMapping(
    $where: AtsStageWithJobRoleGuideTemplateIdsInput!
  ) {
    currentOrganization {
      id
      stages: atsStageWithJobRoleGuideTemplateIds(where: $where) {
        id: atsStageName
        name: atsStageName
        avgPosition: avgAtsStagePosition
        jobRoleGuideTemplateIds
        guideTemplateStageIds
      }
    }
  }
`;

const GET_PLAN_FOR_MAPPING = gql`
  query GetPlanForMapping($id: ID!) {
    currentOrganization {
      id
      guideTemplate: guideTemplateById(id: $id) {
        id
        name
        guideTemplateStages {
          id
          position
          name
        }
      }
    }
  }
`;

interface MapStagesViewProps {
  jobRoleGuideTemplateIds: string[];
  singleJobName?: string;
  planId: string | null;
  mapping: Record<string, Stage[]>;
  setMapping: (arg: Record<string, Stage[]>) => void;
  setIsLoadingPlan: (arg: boolean) => void;
}

function MapStagesView({
  jobRoleGuideTemplateIds,
  singleJobName,
  planId,
  mapping,
  setMapping,
  setIsLoadingPlan,
}: MapStagesViewProps) {
  const [initialized, setInitialized] = useState(false);

  const [variables, setVariables] = useState<GetAtsStagesForMappingVariables>({
    where: { jobRoleGuideTemplateIds, search: "" },
  });

  const { data: nestedPlanData, loading: planDataLoading } = useQuery<
    GetPlanForMapping,
    GetPlanForMappingVariables
  >(GET_PLAN_FOR_MAPPING, {
    variables: { id: planId ?? "" },
  });
  const { data: stagesData } = useQuery<
    GetAtsStagesForMapping,
    GetAtsStagesForMappingVariables
  >(GET_ATS_STAGES_FOR_MAPPING, { variables });
  const name = jobRoleGuideTemplateIds.length > 1 ? undefined : singleJobName;

  const stages = stagesData?.currentOrganization?.stages;
  const planData = nestedPlanData?.currentOrganization?.guideTemplate;
  const plan = useMemo(
    () =>
      planData
        ? {
            id: planData.id,
            name: planData.name,
            guideTemplateStages: planData.guideTemplateStages,
          }
        : null,
    [planData]
  );

  useEffect(() => {
    setIsLoadingPlan(planDataLoading);
  }, [setIsLoadingPlan, planDataLoading]);

  useEffect(() => {
    if (!initialized && stages && plan) {
      const initialMapping: Record<string, Stage[]> = _(stages)
        .filter(["guideTemplateStageIds.length", 1])
        .filter((stage) =>
          _.some(plan.guideTemplateStages, {
            id: stage.guideTemplateStageIds[0],
          })
        )
        .groupBy("guideTemplateStageIds[0]")
        .value();
      setMapping(initialMapping);
      setInitialized(true);
    }
  }, [plan, stages, initialized, setMapping]);

  return !planDataLoading && initialized && plan ? (
    <GuideTemplateMapper
      name={name}
      plan={plan}
      mapping={mapping}
      stages={stages ?? []}
      updateMapping={setMapping}
      filters={variables.where}
      updateFilters={(filters) =>
        setVariables({ ...variables, where: filters })
      }
      forMultipleJobs={jobRoleGuideTemplateIds.length > 1}
    />
  ) : (
    <div className="flex justify-center items-center min-h-[60vh]">
      <LoadingIndicator />
    </div>
  );
}

export default MapStagesView;
